import { useRef, useState } from "react";
import { Image as ImageIcon, Orbit } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const FLAT_IMG =
    "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMDM2MCUyMHBhbm9yYW1hJTIwaG90ZWwlMjBzaG93cm9vbSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzg5MDI2MTY5fDA&ixlib=rb-4.1.0&q=85&w=1600";
const IMMERSIVE_IMG =
    "https://images.unsplash.com/photo-1761602866012-ae9f888255dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxlcXVpcmVjdGFuZ3VsYXIlMjAzNjAlMjBwYW5vcmFtYSUyMGludGVyaW9yJTIwbW9kZXJuJTIwbHV4dXJ5JTIwbG9iYnklMjByb29tJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4OTAyNjE3Nnww&ixlib=rb-4.1.0&q=85&w=1600";

export default function Comparison() {
    const [pos, setPos] = useState(50);
    const trackRef = useRef(null);
    const dragging = useRef(false);

    const update = (clientX) => {
        const r = trackRef.current?.getBoundingClientRect();
        if (!r) return;
        setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
    };

    return (
        <section className="relative py-20 lg:py-32" data-testid="comparison-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHead
                    chapter="02"
                    eyebrow="The Difference"
                    title="WHAT IS A 360° VIRTUAL TOUR?"
                    sub="A photo shows one angle and hopes for the best. A 360° tour hands over the controls — customers look where they want, for as long as they want. Drag the handle and see the difference."
                />

                <Reveal delay={0.15} className="mt-12">
                    <div
                        ref={trackRef}
                        data-testid="comparison-slider"
                        className="relative h-[420px] cursor-ew-resize select-none overflow-hidden rounded-2xl border border-[#00C6FF]/25 sm:h-[520px]"
                        onPointerDown={(e) => {
                            dragging.current = true;
                            update(e.clientX);
                        }}
                        onPointerMove={(e) => dragging.current && update(e.clientX)}
                        onPointerUp={() => (dragging.current = false)}
                        onPointerLeave={() => (dragging.current = false)}
                    >
                        <img
                            src={IMMERSIVE_IMG}
                            alt="360 experience"
                            className="absolute inset-0 h-full w-full object-cover"
                            draggable={false}
                        />
                        <div className="absolute inset-0 bg-[#00C6FF]/10" />
                        <div className="absolute bottom-6 right-6 max-w-[240px] rounded-xl border border-[#00C6FF]/40 bg-[#050B14]/80 p-4 backdrop-blur-md">
                            <div className="flex items-center gap-2 text-[#00C6FF]">
                                <Orbit size={16} className="orbit-ring" />
                                <span className="text-xs font-bold uppercase tracking-[0.2em]">
                                    360° Experience
                                </span>
                            </div>
                            <p className="mt-2 text-xs text-slate-300">
                                Look around, move, zoom and explore the complete space.
                            </p>
                        </div>

                        <div
                            className="absolute inset-0 overflow-hidden"
                            style={{ width: `${pos}%` }}
                        >
                            <img
                                src={FLAT_IMG}
                                alt="Normal photo"
                                className="h-full w-full object-cover grayscale"
                                style={{ width: trackRef.current?.offsetWidth || "100vw", maxWidth: "none" }}
                                draggable={false}
                            />
                            <div className="absolute inset-0 bg-[#050B14]/45" />
                            <div className="absolute bottom-6 left-6 max-w-[240px] rounded-xl border border-white/15 bg-[#050B14]/80 p-4 backdrop-blur-md">
                                <div className="flex items-center gap-2 text-slate-300">
                                    <ImageIcon size={16} />
                                    <span className="text-xs font-bold uppercase tracking-[0.2em]">
                                        Normal Photo
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-slate-400">
                                    Shows only one angle of your business.
                                </p>
                            </div>
                        </div>

                        <div
                            className="absolute inset-y-0 z-10 w-[3px] bg-[#00C6FF] shadow-[0_0_20px_rgba(0,198,255,0.8)]"
                            style={{ left: `${pos}%` }}
                        >
                            <div
                                data-testid="comparison-handle"
                                className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#00C6FF] bg-[#050B14]"
                            >
                                <span className="font-mono-accent text-[10px] font-bold text-[#00C6FF]">
                                    ⇄
                                </span>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
